import { FocusEvent, FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { DateInput } from '@trv-tds/core'
import dayjs from 'dayjs'

type ClaimDatePickerProps = {
  id: string
  trigger?: string
  // Send a date string without time to prevent javascript Date doing system
  // timezone conversions. ie. new Date('01-01-2022') is set as zulu time at 00:00:00.
  // That gives a date of 2021-12-31 in any American timezone and conversion happens
  // natively behind the scenes on getDate(), etc functions.
  // Ex. In any timezone West of England, new Date('2022-01-01').getDate() returns 31.
  value?: string
  disabled?: boolean
  disabledDates?: string
  disabledDatesFunction(date: Date): boolean
  onChange(value: string): void
}

const TdsDatePicker = (props: ClaimDatePickerProps) => {
  const {
    id,
    disabledDates,
    disabled,
    disabledDatesFunction,
    onChange,
    trigger,
    value
  } = props
  const [day, setDay] = useState('')
  const [month, setMonth] = useState('')
  const [year, setYear] = useState('')

  const dateInputRef = useRef<HTMLInputElement>(null)

  const dateInput = useRef<DateInput | null>(null)

  useEffect(() => {
    if (!dateInput.current && dateInputRef.current) {
      dateInput.current = new DateInput(dateInputRef.current)
    }
  }, [])

  // handle onChange event if user edit day/month/year field
  const handleOnChange = useCallback((event: FormEvent<HTMLInputElement>) => {
    if (disabled) {
      return
    }

    event.preventDefault()
    switch (event.currentTarget.name) {
      case 'day':
        setDay(event.currentTarget.value)
        break
      case 'month':
        setMonth(event.currentTarget.value)
        break
      case 'year':
        setYear(event.currentTarget.value)
        break
      default:
        break
    }

    // disable dates for given business condition
    if (disabledDatesFunction) {
      dateInput.current?.setConfig({ disabledDates: [disabledDatesFunction] })
    }
    if (onChange && dateInput.current) onChange(dateInput.current.value.toString())
  }, [disabled, disabledDatesFunction, onChange])

  useEffect(() => {
    // Only re-set state for a prefill, so if date is empty on load.
    const emptyDate = !year && !month && !day
    // check if value is valid dateObject
    if (emptyDate && value && !Number.isNaN(Date.parse(value))) {
      const fullDate = dayjs(value, 'MM/DD/YYYY')
      const yearString = fullDate.year().toString()
      const monthString = (fullDate.month() + 1).toString().length === 1
        ? `0${(fullDate.month() + 1).toString()}`
        : (fullDate.month() + 1).toString()
      const dateOfMonthString = fullDate.date().toString().length === 1
        ? `0${fullDate.date().toString()}`
        : fullDate.date().toString()

      setYear(yearString)
      setMonth(monthString)
      setDay(dateOfMonthString)
    }

    // disable dates for given business condition
    if (disabledDatesFunction) {
      dateInput.current?.setConfig({ disabledDates: [disabledDatesFunction] })
    }
  }, [value, disabledDatesFunction, year, month, day])

  // When a date is pasted in (with the formatt MMDDYYYY), the input onChange events
  // don't happen individually. This on blur to the overall input allows us to set state and update
  // the ref when a date is pasted all at once.
  const handleBlur = useCallback((event: FocusEvent<HTMLDivElement, Element>) => {
    if (!event.currentTarget.innerText) return

    const fullDate = dayjs(event.currentTarget.innerText, 'MM/DD/YYYY')
    if (!fullDate.isValid()) return

    const yearString = fullDate.year().toString()
    const monthString = (fullDate.month() + 1).toString().length === 1
      ? `0${(fullDate.month() + 1).toString()}`
      : (fullDate.month() + 1).toString()
    const dateOfMonthString = fullDate.date().toString().length === 1
      ? `0${fullDate.date().toString()}`
      : fullDate.date().toString()

    setYear(yearString)
    setMonth(monthString)
    setDay(dateOfMonthString)

    // disable dates for given business condition
    if (disabledDatesFunction) {
      dateInput.current?.setConfig({ disabledDates: [disabledDatesFunction] })
    }
    if (dateInput.current && onChange) onChange(dateInput.current.value.toString())
  }, [onChange, disabledDatesFunction])

  return (
    <div>
      <div
        className="tds-date-input"
        ref={dateInputRef}
        aria-invalid={true}
        data-disabled-dates={disabledDates}
        onBlur={handleBlur}
      >
        <span className="tds-date-input__date-part">
          <input
            name="year"
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder="yyyy"
            maxLength={4}
            data-date-part="year"
            aria-label="Year"
            value={year}
            onChange={handleOnChange}
            aria-disabled={disabled}
          />
        </span>
        <span className="tds-date-input__date-part">
          <input
            name="month"
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder="mm"
            maxLength={2}
            data-date-part="month"
            aria-label="Month"
            value={month}
            onChange={handleOnChange}
            aria-disabled={disabled}
          />
        </span>
        <span className="tds-date-input__date-part">
          <input
            id={id}
            name="day"
            type="text"
            inputMode="numeric"
            pattern="\d*"
            placeholder="dd"
            maxLength={2}
            data-date-part="day"
            aria-label="Day"
            value={day}
            onChange={handleOnChange}
            aria-disabled={disabled}
          />
        </span>
        <button
          className="tds-date-input__trigger"
          aria-expanded="false"
          type="button"
          data-trigger={trigger}
          aria-label="Calendar"
          hidden
        ></button>
        <input type="hidden" name="datevalue" />
        <div className="tds-calendar" />
      </div>
    </div>
  )
}

const ClaimDatePicker = ({ id, disabled, ...rest }: ClaimDatePickerProps) => (
  <fieldset className="tds-field" aria-disabled={disabled}>
    <TdsDatePicker id={id} disabled={disabled} {...rest} />
  </fieldset>
)

export {
  ClaimDatePicker
}
